// High-level wrapper combining list display with inline creation.
import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import TaskListBase from '@/features/tasks/components/lists/TaskListBase';
import TaskInputQuick from '@/features/tasks/components/forms/TaskInputQuick';
import ErrorBoundary from '@/components/layout/ErrorBoundary';

const DEFAULT_PROPS = {
  tasks: [],
  onAddTask: () => {},
  isAddingTask: false,
  onCancelNewTask: () => {},
  selectedTaskIds: [],
  onSelectionChange: () => {},
  selectNewTask: true,
  id: null,
  isSortable: false,
  indentationType: 'none',
  plannedParentIds: new Set(),
  waitingForParentIds: new Set(),
  duplicateOrderKeyTaskIds: new Set(),
  view: 'default',
  showBreadcrumbs: false,
};

const TaskList = (props) => {
  const fullProps = { ...DEFAULT_PROPS, ...props };
  const {
    tasks,
    onAddTask,
    isAddingTask,
    onCancelNewTask,
    selectedTaskIds,
    onSelectionChange,
    selectNewTask,
    id,
    isSortable,
    indentationType,
    plannedParentIds,
    waitingForParentIds,
    duplicateOrderKeyTaskIds,
    view,
    showBreadcrumbs,
    ...rest
  } = fullProps;

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [showNewTaskForm, setShowNewTaskForm] = useState(isAddingTask);

  useEffect(() => {
    setShowNewTaskForm(isAddingTask);
  }, [isAddingTask]);

  const handleStartEditing = (taskId) => {
    setEditingTaskId(taskId);
  };

  const handleStopEditing = () => {
    setEditingTaskId(null);
  };

  const handleNewTaskSubmit = (taskData) => {
    onAddTask(taskData);
    setShowNewTaskForm(false);
    if (onCancelNewTask) {
      onCancelNewTask();
    }
  };

  const handleNewTaskCancel = () => {
    setShowNewTaskForm(false);
    if (onCancelNewTask) {
      onCancelNewTask();
    }
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <ErrorBoundary>
        <TaskListBase
          id={id}
          isSortable={isSortable}
          tasks={tasks}
          editingTaskId={editingTaskId}
          onStartEditing={handleStartEditing}
          onStopEditing={handleStopEditing}
          selectedTaskIds={selectedTaskIds}
          onSelectionChange={onSelectionChange}
          selectNewTask={selectNewTask}
          indentationType={indentationType}
          plannedParentIds={plannedParentIds}
          waitingForParentIds={waitingForParentIds}
          duplicateOrderKeyTaskIds={duplicateOrderKeyTaskIds}
          view={view}
          showBreadcrumbs={showBreadcrumbs}
          {...rest}
        />
      </ErrorBoundary>
      {showNewTaskForm ? (
        <TaskInputQuick
          onTaskAdd={handleNewTaskSubmit}
          onBlur={handleNewTaskCancel}
          isInline={true}
        />
      ) : null}
    </Box>
  );
};

export default TaskList;
